'use client';

import { usePageAction } from '@repo/hooks/usePageAction';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { Details } from '@repo/ui-v2/Details';
import type { CSSProperties } from 'react';
import { MdExpandMore } from 'react-icons/md';
import type { ChartLegendItemProps } from '.';
import { DEFAULT_COLORS } from '../constants';

const expandIconClass = css({
  color: 'neutral.light',
  fontSize: 'xxl',
  transitionProperty: '[transform]',
  transitionDuration: '[0.3s]',
  transitionTimingFunction: 'ease',
  _hover: {
    color: 'neutral.darker',
  },
  '[open] > summary &': {
    transform: '[rotate(180deg)]',
    color: 'neutral.darker',
  },
});

export function ChartLegendItemAccordion({ item, index }: ChartLegendItemProps) {
  const trackLegendItemInteraction = usePageAction({
    actionName: 'banking_rating_methodology',
    actionSection: 'banking',
  });

  const legendColor = item.color ?? DEFAULT_COLORS[index];

  return (
    <Details
      variant="simple"
      detailsProps={{
        className: cx(
          'star-rating-categories-legend-item',
          css({
            borderBottomWidth: 'thin',
            borderBottomStyle: 'solid',
            borderBottomColor: 'neutral.lighter2',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            _hover: {
              borderBottomColor: 'var(--legend-color)',
            },
            '&.legend-active': {
              borderBottomColor: 'var(--legend-color)',
            },
          }),
        ),
        style: { '--legend-color': legendColor } as CSSProperties,
        onClick: () => trackLegendItemInteraction(),
      }}
      summaryComponentProps={{
        className: css({
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBlockStart: '3',
          paddingBlockEnd: '3',
        }),
      }}
      SummaryComponent={
        <>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              marginRight: '[50px]',
            })}
          >
            <span
              className={css({
                flexShrink: '[0]',
                display: 'inline-block',
                width: '3.5',
                height: '3.5',
                borderTopLeftRadius: '2',
                borderTopRightRadius: '2',
                borderBottomRightRadius: '2',
                borderBottomLeftRadius: '2',
                backgroundColor: 'var(--legend-color)',
              })}
            />
            <span
              className={css({
                fontSize: 'md',
                fontWeight: 'medium',
                marginLeft: '2',
                color: 'neutral.darker',
              })}
            >
              {item.label}
            </span>
          </div>
          <div
            className={css({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: '2',
              rowGap: '2',
            })}
          >
            <span className={css({ fontSize: 'md', color: 'neutral.dark' })}>
              {item.value}%
            </span>
            <MdExpandMore className={cx(expandIconClass, 'expand-icon')} aria-hidden="true" />
          </div>
        </>
      }
    >
      <p
        className={css({
          textStyle: 'textSmall',
          color: 'neutral.dark',
          paddingBottom: '4',
        })}
      >
        {item.tooltipLabel}
      </p>
    </Details>
  );
}
